import { ExternalLink, FileText, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { mediaUrl } from '@/core/api';
import { formatBytes } from '@/lib/format';
import { artifactEmbedUrl } from '../lib/embeds';
import type { SubmissionArtifactDto } from '../types';

function LinkArtifact({ artifact }: { artifact: SubmissionArtifactDto }) {
  const href = artifact.url ?? '';
  const embed = artifactEmbedUrl(href);

  if (embed) {
    return (
      <figure className="space-y-1">
        <iframe
          src={embed}
          title={href}
          loading="lazy"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="aspect-video w-full rounded-md border"
        />
        <figcaption className="text-muted-foreground text-xs break-all">{href}</figcaption>
      </figure>
    );
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="text-primary inline-flex items-center gap-1.5 text-sm break-all underline underline-offset-4"
    >
      <ExternalLink className="size-4 shrink-0" />
      {href}
    </a>
  );
}

function UploadedArtifact({ artifact }: { artifact: SubmissionArtifactDto }) {
  const { t, i18n } = useTranslation(['homework']);
  if (!artifact.url) return null;
  const src = mediaUrl(artifact.url);

  if (artifact.kind === 'image') {
    return (
      <img
        src={src}
        alt={artifact.original_filename}
        loading="lazy"
        className="max-h-96 max-w-full rounded-md border object-contain"
      />
    );
  }
  if (artifact.kind === 'video') {
    return (
      // eslint-disable-next-line jsx-a11y/media-has-caption -- pupil uploads carry no caption tracks
      <video controls preload="metadata" src={src} className="w-full rounded-md border" />
    );
  }
  if (artifact.kind === 'audio') {
    // eslint-disable-next-line jsx-a11y/media-has-caption -- pupil uploads carry no caption tracks
    return <audio controls preload="metadata" src={src} className="w-full" />;
  }
  return (
    <a
      href={src}
      download={artifact.original_filename}
      className="bg-muted/60 hover:bg-muted flex items-center gap-2 rounded-md border px-3 py-2 text-sm"
    >
      <FileText className="text-muted-foreground size-4 shrink-0" />
      <span className="truncate font-medium">{artifact.original_filename}</span>
      {typeof artifact.size === 'number' ? (
        <span className="text-muted-foreground shrink-0">
          {formatBytes(artifact.size, i18n.language)}
        </span>
      ) : null}
      <span className="sr-only">{t('homework:review.attachments')}</span>
    </a>
  );
}

interface ArtifactsViewProps {
  artifacts: SubmissionArtifactDto[];
  /** Provided on the pupil's own editable submission. */
  onRemove?: (artifact: SubmissionArtifactDto) => void;
  removing?: boolean;
}

/**
 * Submission attachments rendered in place: images inline, video/audio in
 * native players, files as download chips, and links as chips — with a
 * privacy-enhanced embed when a YouTube or Loom id can be extracted.
 */
export function ArtifactsView({ artifacts, onRemove, removing = false }: ArtifactsViewProps) {
  const { t } = useTranslation(['homework']);
  if (artifacts.length === 0) {
    return <p className="text-muted-foreground text-sm">{t('homework:review.noAttachments')}</p>;
  }
  return (
    <ul className="space-y-3">
      {artifacts.map(artifact => (
        <li key={artifact.public_id} className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            {artifact.kind === 'link' ? (
              <LinkArtifact artifact={artifact} />
            ) : (
              <UploadedArtifact artifact={artifact} />
            )}
          </div>
          {onRemove ? (
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              disabled={removing}
              aria-label={t('homework:detail.removeAttachment')}
              onClick={() => onRemove(artifact)}
            >
              <Trash2 />
            </Button>
          ) : null}
        </li>
      ))}
    </ul>
  );
}
